import { MathUtils, Vector3 } from 'three';
import { EAttributeType } from '../@types/AttributeTypes';
import { ITexture } from '../@types/ContextProviderRelated';
import { EAssetType } from '../@types/FilesAPIModels';
import AttributeModel from '../models/standard/AttributeModel';
import FilesAPI2DModel from '../models/standard/FilesAPI2DModel';
import FilesAPIProductModel from '../models/standard/FilesAPIProductModel';
import Model2DModel, { IModel2D } from '../models/standard/Model2DModel';
import { ObjectModel } from '../models/standard/ObjectModel';
import ProductModel, { IProduct } from '../models/standard/ProductModel';
import filesApiService from '../utils/filesApiService';

/**
 * Fetches SVG and dimensions, then returns a full ProductModel ready for the scene.
 */
export const createProductModelFromApi = async (
  apiProduct: FilesAPIProductModel,
  textureInfo?: ITexture
): Promise<IProduct | null> => {
  try {
    const modelAssets = apiProduct.modelAssets.filter((asset) => asset.assetType === EAssetType.MODEL);
    if (modelAssets.length === 0) return null;

    const firstAsset = modelAssets[0];
    const modelData = await filesApiService.getProductModelById(apiProduct.id, firstAsset.id);
    if (!modelData) return null;

    const svgPath = await filesApiService.getSVGFileFromProduct(apiProduct.id, firstAsset.id);

    // Calculate props
    const initialRotation = MathUtils.degToRad(Number(modelData.rotation2D) || 0);
    const modelLength = modelData.length ?? 0;
    const modelDepth = modelData.depth ?? 0;

    // Attributes
    const parsedAttributes = (apiProduct.attributes || []).map((attrObj) => {
        const wrapper = attrObj as any;
        const def = wrapper.attribute || wrapper;
        const value = wrapper.value ?? def.value;
        const unit = wrapper.unit ?? def.unit;
        const typeMap: any = { TEXT: EAttributeType.TEXT, NUMBER: EAttributeType.NUMBER, BOOL: EAttributeType.BOOL, SELECTION: EAttributeType.SELECTION, PLAIN: EAttributeType.PLAIN };
        const type = typeMap[def.type] || EAttributeType.TEXT;

        return new AttributeModel(
            Number(def.id), def.key || '', value, null, type, unit,
            wrapper.hidden ?? false, wrapper.forced ?? false, def.ownerId, def.userId, def.publicFlag, def.localization, def.defaults, def.validation, def.unitFamily
        );
    });

    const parsedObjects = (apiProduct.objects || []).map((obj) => ObjectModel.fromJSON(obj));

    return new ProductModel(
      apiProduct.id.toString(),
      apiProduct.name,
      svgPath,
      modelLength,
      0,
      modelDepth,
      new Vector3(0,0,0), // Default position, will be updated by ghost placement
      initialRotation,
      undefined, undefined, 1.5,
      textureInfo,
      parsedAttributes,
      parsedObjects,
      apiProduct.description,
      apiProduct.manufacturer,
      undefined, // Generates new UUID
      apiProduct.country
    );
  } catch (e) {
    console.error("Failed to create product model", e);
    return null;
  }
};

/**
 * Fetches SVG and dimensions for 2D Models
 */
export const createModel2DFromApi = async (apiModel: FilesAPI2DModel): Promise<IModel2D | null> => {
    try {
        const svgPath = await filesApiService.getSVGFileFromModel(apiModel.id);
        const initialRotation = MathUtils.degToRad(Number(apiModel.rotation2D) || 0);

        return new Model2DModel(
            apiModel.id,
            apiModel.name,
            svgPath,
            apiModel.fileFormat,
            apiModel.fileSize,
            apiModel.length,
            apiModel.depth,
            apiModel.object,
            new Vector3(0,0,0),
            initialRotation,
            undefined,
            undefined,
            undefined // Generates UUID
        );
    } catch (e) {
        console.error("Failed to create 2D model", e);
        return null;
    }
}